/** @format */
import { Transition } from '@mpb-com/feather/components/atoms/transition';
import { useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
} from 'react';
import styled from 'styled-components';

import { ProductAddToQuoteLinkSkeleton } from '@toucan/components/views/product/components/product-add-to-quote-link/skeleton';

import CheckmarkIcon from '@toucan/icons/checkmark.svg';
import PlusIcon from '@toucan/icons/plus.svg';
import TradeArrowIcon from '@toucan/icons/trade-arrow.svg';

import { SellFormContext } from '@toucan/client/global/application';
import { Link } from '@toucan/client/shared/components/link';
import { useTradeForThisHandler } from '@toucan/features/product/hooks/use-trade-for-this-handler';

import { DEFAULT_TEST_ID } from './constants';
import { styles, wrapperStyles } from './product-add-to-quote-link.styles';
import {
    ProductAddToQuoteLinkProps,
    StyledProductAddToQuoteLinkProps,
} from './types';

const StyledProductAddToQuoteLink = styled(
    Link
)<StyledProductAddToQuoteLinkProps>`
    ${styles}
`;

export const StyledProductAddToQuoteLinkWrapper = styled('div')`
    ${wrapperStyles}
`;

export const ProductAddToQuoteLink: FunctionComponent<
    ProductAddToQuoteLinkProps
> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    productData,
    refreshProductData,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { handleTradeForThis, hasOpenQuote, isInOpenQuote, isAddingToQuote } =
        useTradeForThisHandler({ productData, refreshProductData });
    const { sellForm } = useContext(SellFormContext);

    const linkVariants: {
        prefix: ReactNode;
        text: string;
        isVisible: boolean;
    }[] = [
        {
            prefix: (
                <CheckmarkIcon
                    data-testid={`${dataTestId}__checkmark-icon`}
                    className="add-to-quote-icon checkmark"
                />
            ),
            text: t('Added to quote'),
            isVisible: isInOpenQuote,
        },
        {
            prefix: (
                <PlusIcon
                    data-testid={`${dataTestId}__plus-icon`}
                    className="add-to-quote-icon plus"
                />
            ),
            text: t('Add to open quote'),
            isVisible: !isInOpenQuote && hasOpenQuote,
        },
        {
            prefix: (
                <Fragment>
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow top"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow top-hidden"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow bottom"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow bottom-hidden"
                    />
                </Fragment>
            ),
            text: t('Lower the cost when you trade'),
            isVisible: !isInOpenQuote && !hasOpenQuote,
        },
    ];

    if (!sellForm.hasInitialised) {
        return <ProductAddToQuoteLinkSkeleton />;
    }

    return (
        <StyledProductAddToQuoteLinkWrapper>
            {linkVariants.map(({ isVisible, prefix, text }) => (
                <Transition key={text} durationMs={600} isVisible={isVisible}>
                    {({ props, isAnimating }) => (
                        <StyledProductAddToQuoteLink
                            {...otherProps}
                            {...props}
                            $isAnimating={isAnimating}
                            $isAddingToQuote={isAddingToQuote}
                            data-testid={dataTestId}
                            disabled={isAddingToQuote}
                            onClick={handleTradeForThis}
                            prefix={prefix}
                        >
                            {text}
                        </StyledProductAddToQuoteLink>
                    )}
                </Transition>
            ))}
        </StyledProductAddToQuoteLinkWrapper>
    );
};
